import { useCallback, useEffect, useRef, useState } from 'react';

const API_SRC = 'https://www.youtube.com/iframe_api';
// The no-cookie host sets no tracking cookies until the video plays
const PLAYER_HOST = 'https://www.youtube-nocookie.com';
const TICK_MS = 100;

// YT.PlayerState values
const ENDED = 0;
const PLAYING = 1;
const BUFFERING = 3;

let apiPromise = null;

/** Loads YouTube's IFrame Player API once for the whole app, resolving with `window.YT` */
export const loadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve(window.YT);
    };

    const script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    script.onerror = () => {
      // Let a later mount try again, say once the network is back
      apiPromise = null;
      reject(new Error('YouTube player API failed to load'));
    };
    document.head.appendChild(script);
  });

  return apiPromise;
};

/**
 * A YouTube player mounted into `mountRef`, driven from the page. `onTick(time)` runs every 100 ms
 * while it plays, which is what an A–B loop hangs off; `onEnded` when the video runs out.
 */
const useYouTubePlayer = (videoId, { onTick, onEnded } = {}) => {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const callbacksRef = useRef({ onTick, onEnded });
  callbacksRef.current = { onTick, onEnded };

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!videoId || !mount) return undefined;

    let cancelled = false;
    // YT.Player replaces the element it's given, so it gets one React doesn't own
    const target = document.createElement('div');
    mount.appendChild(target);

    setReady(false);
    setFailed(false);
    setPlaying(false);
    setTime(0);
    setDuration(0);

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return;

        playerRef.current = new YT.Player(target, {
          host: PLAYER_HOST,
          videoId,
          width: '100%',
          height: '100%',
          playerVars: { playsinline: 1, rel: 0 },
          events: {
            onReady: ({ target: player }) => {
              if (cancelled) return;
              setReady(true);
              setDuration(player.getDuration() || 0);
              setRates(player.getAvailablePlaybackRates?.() || []);
              setRateState(player.getPlaybackRate?.() || 1);
            },
            onStateChange: ({ data, target: player }) => {
              if (cancelled) return;
              // Buffering keeps what it was, so a seek or a song loading doesn't flash Play and back
              if (data !== BUFFERING) setPlaying(data === PLAYING);
              // Duration is often 0 until the video has started
              if (data === PLAYING) setDuration(player.getDuration() || 0);
              if (data === ENDED) callbacksRef.current.onEnded?.();
            },
            onPlaybackRateChange: ({ data }) => !cancelled && setRateState(data),
          },
        });
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      playerRef.current?.destroy?.();
      playerRef.current = null;
      target.remove();
      mount.replaceChildren();
    };
  }, [videoId]);

  useEffect(() => {
    if (!playing) return undefined;

    const id = setInterval(() => {
      const now = playerRef.current?.getCurrentTime?.() ?? 0;
      setTime(now);
      callbacksRef.current.onTick?.(now);
    }, TICK_MS);

    return () => clearInterval(id);
  }, [playing]);

  const play = useCallback(() => playerRef.current?.playVideo(), []);
  const pause = useCallback(() => playerRef.current?.pauseVideo(), []);
  const seek = useCallback((seconds) => {
    playerRef.current?.seekTo(seconds, true);
    setTime(seconds);
  }, []);
  const setRate = useCallback((next) => {
    playerRef.current?.setPlaybackRate(next);
    setRateState(next);
  }, []);
  // Swaps the video in the same player and plays it. Moving on in a player that's already playing is what
  // lets a playlist go to its next song on iOS, where a new player would need another tap
  const load = useCallback((id) => {
    playerRef.current?.loadVideoById(id);
    // Not playing until the new video says so, or the old one's Playing shows on the next song early
    setPlaying(false);
    setTime(0);
    setDuration(0);
  }, []);
  // The player's own clock, not the last tick's, for "set A here" taps between ticks
  const currentTime = useCallback(() => playerRef.current?.getCurrentTime?.() ?? 0, []);

  return {
    mountRef,
    ready,
    failed,
    playing,
    time,
    duration,
    rate,
    rates,
    play,
    pause,
    seek,
    setRate,
    load,
    currentTime,
  };
};

export default useYouTubePlayer;
